import { FilterMatchMode, FilterOperator } from "primereact/api";
import { Column, ColumnProps } from "primereact/column";
import {
  DataTable,
  DataTableFilterMeta,
  DataTableFilterMetaData,
} from "primereact/datatable";
import { InputText } from "primereact/inputtext";
import React, { useCallback, useEffect, useRef, useState } from "react";

export interface ColumnConfig extends Partial<ColumnProps> {
  label: string;
  value: string;
}

interface CustomerDataTableProps {
  data: any[];
  loading: boolean;
  columns: ColumnConfig[];
  searchable?: boolean;
  onRowClick?: (a: any) => void;
  actionButtons?: React.ReactNode;
  emptyMessage?: string;
  filterDisplay?: "menu" | "row";
  onValueChange?: (value: any[]) => void;
  // The fields the search box matches; by default the columns' fields.
  globalFilterFields?: string[];
}

const ReusableList: React.FC<CustomerDataTableProps> = ({
  data,
  loading,
  columns,
  searchable = false,
  actionButtons = <></>,
  onRowClick,
  emptyMessage = undefined,
  filterDisplay,
  onValueChange,
  globalFilterFields,
}) => {
  const [filters, setFilters] = useState<DataTableFilterMeta>({});
  const [globalFilterValue, setGlobalFilterValue] = useState("");
  const dt = useRef(null);

  const initFilters = useCallback(() => {
    // Dynamically initialize filters for each column based on its value (accessor key)
    const newFilters: DataTableFilterMeta = {
      global: { value: null, matchMode: FilterMatchMode.CONTAINS },
    };
    columns.forEach((col) => {
      if (col.value) {
        if (col.value.includes("date")) {
          newFilters[col.value] = {
            operator: FilterOperator.AND,
            constraints: [{ value: null, matchMode: FilterMatchMode.DATE_IS }],
          };
        } else if (col.value.includes("activity")) {
          newFilters[col.value] = {
            value: null,
            matchMode: FilterMatchMode.BETWEEN,
          };
        } else if (col.value.includes("representative")) {
          newFilters[col.value] = {
            value: null,
            matchMode: FilterMatchMode.IN,
          };
        } else {
          newFilters[col.value] = {
            operator: FilterOperator.AND,
            constraints: [
              {
                value: null,
                // A column can ask for its own match mode, e.g. EQUALS for a dropdown filter.
                matchMode:
                  (col.filterMatchMode as DataTableFilterMetaData["matchMode"]) ??
                  FilterMatchMode.STARTS_WITH,
              },
            ],
          };
        }
      }
    });

    setFilters(newFilters);
    setGlobalFilterValue("");
  }, [columns]);

  useEffect(() => {
    initFilters();

    // Cleanup function to reset filters and global filter value on unmount
    return () => {
      setFilters({});
      setGlobalFilterValue("");
    };
  }, [initFilters]);

  const onGlobalFilterChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { value } = e.target;
    let _filters = { ...filters };
    (_filters["global"] as any).value = value;
    setFilters(_filters);
    setGlobalFilterValue(value);

    if (onValueChange) {
      if (!value) {
        onValueChange(data);
      } else {
        const lower = value.toLowerCase();
        onValueChange(
          data.filter((row) =>
            Object.values(row).some((field) =>
              String(field ?? "").toLowerCase().includes(lower)
            )
          )
        );
      }
    }
  };

  const renderHeader = () => {
    return (
      <div className="w-full flex flex-wrap gap-2 align-items-center justify-content-between">
        {searchable && (
          <span className="p-input-icon-left w-full sm:w-20rem flex-order-1 sm:flex-order-0">
            <i className="pi pi-search ml-3"></i>
            <InputText
              value={globalFilterValue}
              onChange={onGlobalFilterChange}
              placeholder="Search"
              className="pl-6 w-full"
            />
          </span>
        )}
        {actionButtons && (
          <div className="w-full sm:w-auto flex-order-0 sm:flex-order-1">
            {actionButtons}
          </div>
        )}
      </div>
    );
  };

  const header = renderHeader();

  return (
    <DataTable
      className="mt-4 -mx-2 cursor-pointer"
      ref={dt}
      value={data}
      header={header}
      paginator
      rows={10}
      onRowClick={onRowClick}
      currentPageReportTemplate="Showing {first} to {last} of {totalRecords} entries"
      rowsPerPageOptions={[10, 25, 50]}
      filters={filters}
      globalFilterFields={globalFilterFields}
      filterDisplay={filterDisplay}
      loading={loading}
      rowHover
      emptyMessage={emptyMessage}
    >
      {columns.map((col, index) => (
        <Column
          key={col.value || index}
          field={col.value}
          header={col.label}
          {...col}
        />
      ))}
    </DataTable>
  );
};

export default ReusableList;
